import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PageHeader from '@/shared/components/PageHeader';
import { Button, Card, InputNumber, Modal, Select, Space, Table, Tag } from '@/shared/ui/primitives';
import { CheckOutlined, CloseOutlined, LinkOutlined, CloudDownloadOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import type { ColumnsType } from '@/shared/ui/subpath-types';
import { UserSelect } from '@/shared/ui/entity-selects';
import { importApi, type AssignmentMode } from '@/features/import/api/import.api';
import { importedAdsApi, type ImportedAd } from '../api/importedAds.api';

const SOURCE_COLORS: Record<string, string> = {
  ROOMATY: 'geekblue',
  HARAJ: 'volcano',
  ROOMYTK: 'purple',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'error',
  ARCHIVED: 'default',
};

// Sources the platform imports from. Sources without an importer yet simply
// produce no rows until their importer lands.
const SOURCES = ['ROOMATY', 'HARAJ', 'ROOMYTK'];
const STATUSES = ['DRAFT', 'PUBLISHED', 'REJECTED', 'ARCHIVED'];

export default function ImportedAdsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pageSize = 20;
  const [page, setPage] = useState(0);
  const [source, setSource] = useState<string>('');
  const [status, setStatus] = useState<string>('DRAFT');
  // Keep the keys exactly as the Table emits them (strings) so the checkbox
  // "checked" comparison matches; convert to numbers only at the API boundary.
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);

  const listQuery = useQuery({
    queryKey: ['imported-ads', { page, source, status }],
    queryFn: () => importedAdsApi.list({ page, size: pageSize, source, status }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['imported-ads'] });

  const approveMutation = useMutation({
    mutationFn: (id: number) => importedAdsApi.approve(id),
    onSuccess: () => { toast.success(t('importedAds.toasts.approved')); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => importedAdsApi.reject(id),
    onSuccess: () => { toast.success(t('importedAds.toasts.rejected')); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: number[]) => importedAdsApi.bulkApprove(ids),
    onSuccess: (affected: number) => {
      toast.success(t('importedAds.toasts.bulkApproved', { count: affected }));
      setSelectedKeys([]);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  // Confirm before publishing a batch; the dialog always states how many ads.
  const confirmApprove = (ids: number[]) => {
    if (!ids.length) return;
    Modal.confirm({
      title: t('importedAds.bulkApproveTitle'),
      content: t('importedAds.bulkApproveConfirm', { count: ids.length }),
      okText: t('importedAds.bulkApproveOk', { count: ids.length }),
      cancelText: t('common.cancel'),
      onOk: () => bulkApproveMutation.mutateAsync(ids),
    });
  };

  const approveAll = async () => {
    const ids = await importedAdsApi.allDraftIds({ source });
    if (!ids.length) { toast(t('importedAds.noDrafts')); return; }
    confirmApprove(ids);
  };

  // ── Roomaty import modal (city + max ads + assignment), same options as before ──
  const [roomatyOpen, setRoomatyOpen] = useState(false);
  const [rCityId, setRCityId] = useState<number | null>(null);
  const [rMaxAds, setRMaxAds] = useState<number | null>(null);
  const [rMode, setRMode] = useState<AssignmentMode>('MATCH_OR_CREATE');
  const [rOwnerId, setROwnerId] = useState<number | null>(null);

  const supportedCitiesQuery = useQuery({
    queryKey: ['roomaty-supported-cities'],
    queryFn: () => importApi.roomatySupportedCities(),
    enabled: roomatyOpen,
  });

  const roomatyMutation = useMutation({
    mutationFn: () => importApi.runRoomaty({
      cityId: rCityId as number,
      maxAds: rMaxAds ?? undefined,
      assignmentMode: rMode,
      ownerUserId: rMode === 'ASSIGN_TO_USER' ? rOwnerId : null,
      status: 'DRAFT',
    }),
    onSuccess: (run: any) => {
      toast.success(t('importedAds.import.roomatyDone', {
        imported: run?.imported ?? 0, skipped: run?.skipped ?? 0, failed: run?.failed ?? 0,
      }));
      setRoomatyOpen(false);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  const roomatyValid = rCityId != null && (rMode !== 'ASSIGN_TO_USER' || rOwnerId != null);

  const runHarajMutation = useMutation({
    mutationFn: () => importedAdsApi.runHaraj(),
    onSuccess: (res: any) => {
      toast.success(t('importedAds.import.harajDone', {
        inserted: res?.inserted ?? 0, updated: res?.updated ?? 0, filtered: res?.filtered ?? 0,
      }));
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  const runRoomytkMutation = useMutation({
    mutationFn: () => importedAdsApi.runRoomytk(),
    onSuccess: (res: any) => {
      toast.success(t('importedAds.import.harajDone', {
        inserted: res?.inserted ?? 0, updated: res?.updated ?? 0, filtered: res?.filtered ?? 0,
      }));
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('importedAds.toasts.actionFailed')),
  });

  const records: ImportedAd[] = listQuery.data?.content || [];
  const totalItems = listQuery.data?.totalItems || 0;

  const columns: ColumnsType<ImportedAd> = [
    { title: t('common.id'), dataIndex: 'id', width: 80 },
    {
      title: t('importedAds.columns.ad'),
      width: 280,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700 }}>{r.title || t('importedAds.untitled')}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
            {[r.ownerName, r.ownerPhone].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      ),
    },
    {
      title: t('importedAds.columns.source'),
      dataIndex: 'importSource',
      width: 120,
      render: (v: string) => <Tag color={SOURCE_COLORS[v] || 'default'}>{v || '—'}</Tag>,
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      width: 130,
      render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    { title: t('common.city'), dataIndex: 'cityName', width: 140, render: (v) => v || '—' },
    { title: t('importedAds.columns.importedAt'), dataIndex: 'importedAt', width: 170, render: (v) => (v ? formatDateTime(v) : '—') },
    {
      title: t('common.actions'),
      width: 220,
      render: (_, r) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            disabled={r.status === 'PUBLISHED'}
            loading={approveMutation.isPending && approveMutation.variables === r.id}
            onClick={() => approveMutation.mutate(r.id)}
          >
            {t('importedAds.approve')}
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            disabled={r.status === 'REJECTED'}
            loading={rejectMutation.isPending && rejectMutation.variables === r.id}
            onClick={() => rejectMutation.mutate(r.id)}
          >
            {t('importedAds.reject')}
          </Button>
          {r.importSourceUrl && (
            <Button
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => window.open(r.importSourceUrl!, '_blank', 'noopener,noreferrer')}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="imported-ads-page">
      <PageHeader
        title={t('importedAds.title')}
        subtitle={t('importedAds.subtitle')}
        action={
          <Space wrap>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              onClick={() => setRoomatyOpen(true)}
            >
              {t('importedAds.import.roomaty')}
            </Button>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              loading={runHarajMutation.isPending}
              onClick={() => runHarajMutation.mutate()}
            >
              {t('importedAds.import.haraj')}
            </Button>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              loading={runRoomytkMutation.isPending}
              onClick={() => runRoomytkMutation.mutate()}
            >
              {t('importedAds.import.roomytk')}
            </Button>
            <Button type="text" icon={<LinkOutlined />} onClick={() => navigate('/external-ads')}>
              {t('importedAds.harajReview')}
            </Button>
          </Space>
        }
      />

      <Card style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            placeholder={t('importedAds.columns.source')}
            allowClear
            style={{ minWidth: 180 }}
            value={source || undefined}
            onChange={(v: any) => { setSource(v || ''); setPage(0); }}
            options={SOURCES.map((v) => ({ value: v, label: v }))}
          />
          <Select
            placeholder={t('common.status')}
            allowClear
            style={{ minWidth: 180 }}
            value={status || undefined}
            onChange={(v: any) => { setStatus(v || ''); setPage(0); setSelectedKeys([]); }}
            options={STATUSES.map((v) => ({ value: v, label: v }))}
          />
          <div style={{ flex: 1 }} />
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={selectedKeys.length === 0}
            loading={bulkApproveMutation.isPending}
            onClick={() => confirmApprove(selectedKeys.map(Number))}
          >
            {t('importedAds.approveSelected', { count: selectedKeys.length })}
          </Button>
          <Button
            icon={<CheckOutlined />}
            loading={bulkApproveMutation.isPending}
            onClick={approveAll}
          >
            {t('importedAds.approveAll')}
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={records}
        loading={listQuery.isLoading}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys: (string | number)[]) => setSelectedKeys(keys),
        }}
        scroll={{ x: 1100 }}
        locale={{ emptyText: t('common.noData') }}
        pagination={{
          current: page + 1,
          pageSize,
          total: totalItems,
          onChange: (p) => setPage(p - 1),
          showSizeChanger: false,
          showTotal: (total, range) => t('common.showingRange', { from: range[0], to: range[1], total }),
        }}
      />

      <Modal
        open={roomatyOpen}
        width={560}
        title={t('importedAds.import.roomatyTitle')}
        okText={t('importedAds.import.roomatyOk')}
        cancelText={t('common.cancel')}
        confirmLoading={roomatyMutation.isPending}
        okButtonProps={{ disabled: !roomatyValid }}
        onOk={() => roomatyMutation.mutate()}
        onCancel={() => setRoomatyOpen(false)}
      >
        <div className="ds-form">
          <div className="ds-form-item">
            <label className="ds-form-label required">{t('importedAds.import.city')}</label>
            <Select
              showSearch
              style={{ width: '100%' }}
              loading={supportedCitiesQuery.isLoading}
              placeholder={t('importedAds.import.cityPlaceholder')}
              value={rCityId ?? undefined}
              onChange={(v: any) => setRCityId(v ?? null)}
              options={(supportedCitiesQuery.data?.cities ?? []).map((c) => ({
                value: c.id,
                label: c.nameAr || c.nameEn,
              }))}
            />
          </div>
          <div className="ds-form-item">
            <label className="ds-form-label">{t('importedAds.import.maxAds')}</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder={t('importedAds.import.maxAdsPlaceholder')}
              value={rMaxAds ?? undefined}
              onChange={(v) => setRMaxAds(v ?? null)}
            />
          </div>
          <div className="ds-form-item">
            <label className="ds-form-label">{t('importedAds.import.mode')}</label>
            <Select
              style={{ width: '100%' }}
              value={rMode}
              onChange={(v: any) => setRMode(v as AssignmentMode)}
              options={[
                { value: 'MATCH_OR_CREATE', label: t('importedAds.import.modeMatch') },
                { value: 'ASSIGN_TO_USER', label: t('importedAds.import.modeAssign') },
              ]}
            />
          </div>
          {rMode === 'ASSIGN_TO_USER' && (
            <div className="ds-form-item">
              <label className="ds-form-label required">{t('importedAds.import.owner')}</label>
              <UserSelect value={rOwnerId ?? undefined} onChange={(v: any) => setROwnerId(v ?? null)} />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
